import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Center,
  Text,
  VStack,
   HStack,
  Badge,
  Icon,
  Tooltip,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ArrowUpIcon, ArrowDownIcon } from "@chakra-ui/icons";
import axios from "axios";
import proxy from "../../proxy";

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useBreakpointValue({ base: true, md: false });

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const response = await axios.get(`${proxy}server_mts/Transactions.php`, {
          headers: { "Cache-Control": "no-cache" },
        });

        if (!response.data || !Array.isArray(response.data)) {
          throw new Error("Invalid transactions data.");
        }

        setTransactions(response.data);
      } catch (err) {
        setError("Unable to retrieve the transactions.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactions();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diff === 0) return "Today";
    if (diff === 1) return "Yesterday";
    if (diff < 7) return `${diff} days ago`;
    if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`;

    return formatDate(dateString);
  };


  if (isLoading) {
    return (
      <Center minH="50vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="teal.300" thickness="4px" />
          <Text color="gray.400">Loading transactions...</Text>
        </VStack>
      </Center>
    );
  }

  if (error) {
    return (
      <Center minH="50vh">
        <VStack spacing={3}>
          <Text fontSize="5xl">⚠️</Text>
          <Text color="red.400" fontWeight="bold">
            {error}
          </Text>
        </VStack>
      </Center>
    );
  }

return (
  <Box maxW="1300px" mx="auto" p={6}>
    <VStack spacing={8} align="stretch">

      {/* HEADER */}
      <Box textAlign="center">
        <Heading
          size="md"
          color="white"
          letterSpacing="1px"
          mb={1}
          fontWeight="extrabold"
        >
          Recent Transactions
        </Heading>
        <Text color="whiteAlpha.600" fontSize="sm">
          Latest inflows & outflows recorded in your wallet
        </Text>
      </Box>

      {/* TABLE CARD */}
      <Box
  p={5}
  borderRadius="2xl"
  bg="linear-gradient(135deg, rgba(26,32,44,0.97), rgba(45,55,72,0.97))"
  border="1px solid rgba(255,255,255,0.08)"
  backdropFilter="blur(18px)"
  boxShadow="0 15px 40px rgba(0,0,0,0.45)"
  overflowX="auto"
  position="relative"
>
  {/* Animated Top Border */}
  <Box
    position="absolute"
    top={0}
    left={0}
    right={0}
    height="3px"
    bgGradient="linear(to-r, teal.300, cyan.400)"
    opacity={0.8}
    sx={{
      animation: "shine 3s ease-in-out infinite",
      "@keyframes shine": {
        "0%,100%": { opacity: 0.4 },
        "50%": { opacity: 1 },
      },
    }}
  />

  {/* Title */}
  <Text
    textAlign="center"
    mb={5}
    color="teal.200"
    fontSize="sm"
    fontWeight="bold"
    letterSpacing="1px"
  >
    📥 Latest 5 Transactions
  </Text>

  {/* ✅ CONDIȚIE PENTRU MOBILE vs DESKTOP */}
  {isMobile ? (
    // 📱 MOBILE VIEW - Cards
    <VStack spacing={3} align="stretch">
      {transactions.map((tx, index) => {
        const isOut = Number(tx.out_in) === 0;
        return (
          <Box
            key={index}
            p={4}
            bg="rgba(255,255,255,0.03)"
            borderRadius="xl"
            border="1px solid rgba(255,255,255,0.08)"
            transition="all 0.25s"
            _hover={{
              bg: "rgba(255,255,255,0.06)",
              transform: "translateY(-2px)",
              boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
            }}
          >
            {/* Header: Type Badge + Date */}
            <HStack justify="space-between" mb={3}>
              <Badge
                px={3}
                py={1}
                borderRadius="full"
                display="inline-flex"
                alignItems="center"
                gap={1.5}
                bg={
                  isOut
                    ? "linear-gradient(135deg,#FC8181,#FEB2B2)"
                    : "linear-gradient(135deg,#48BB78,#68D391)"
                }
                boxShadow={
                  isOut
                    ? "0 0 12px rgba(252,129,129,0.4)"
                    : "0 0 12px rgba(72,187,120,0.4)"
                }
                color="white"
                fontSize="xs"
                fontWeight="bold"
              >
                <Icon as={isOut ? ArrowDownIcon : ArrowUpIcon} boxSize={3} />
                {isOut ? "Outflow" : "Inflow"}
              </Badge>
              
              <Tooltip label={formatDate(tx.date_operation)} hasArrow>
                <Text fontSize="xs" color="gray.400" fontWeight="medium">
                  {getRelativeTime(tx.date_operation)}
                </Text>
              </Tooltip>
            </HStack>

            {/* Amount + Asset */}
            <HStack justify="space-between" align="baseline">
              <Text
                fontSize="2xl"
                fontWeight="black"
                color={isOut ? "red.300" : "green.300"}
                textShadow={
                  isOut
                    ? "0 0 10px rgba(255,90,90,0.3)"
                    : "0 0 10px rgba(90,255,140,0.3)"
                }
              >
                {isOut ? "-" : "+"}
                {tx.amount}
              </Text>
              
              <VStack spacing={1} align="end">
                <Badge
                  px={2.5}
                  py={0.5}
                  borderRadius="full"
                  bg="linear-gradient(135deg,#9F7AEA,#B794F4)"
                  boxShadow="0 0 12px rgba(159,122,234,0.35)"
                  color="white"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  {tx.symbol_crypto}
                </Badge>
                
                <Badge
                  px={2.5}
                  py={0.5}
                  borderRadius="full"
                  bg="linear-gradient(135deg,#0BC5EA,#00B5D8)"
                  boxShadow="0 0 12px rgba(0,180,216,0.4)"
                  color="white"
                  fontSize="2xs"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                >
                  💾 Ledger
                </Badge>
              </VStack>
            </HStack>
          </Box>
        );
      })}
    </VStack>
  ) : (
    // 💻 DESKTOP VIEW - Table (codul tău original)
    <Table size="md" variant="simple">
      <Thead>
        <Tr>
          {["Date", "Type", "Amount", "Asset", "Wallet"].map((h) => (
            <Th
              key={h}
              color="teal.200"
              fontSize="xs"
              textAlign="center"
              py={3}
              borderBottom="1px solid rgba(255,255,255,0.08)"
            >
              {h}
            </Th>
          ))}
        </Tr>
      </Thead>

      <Tbody>
        {transactions.map((tx, index) => {
          const isOut = Number(tx.out_in) === 0;

          return (
            <Tr
              key={index}
              transition="0.25s"
              _hover={{
                bg: "rgba(255,255,255,0.05)",
                transform: "scale(1.005)",
                boxShadow: "0 8px 25px rgba(0,0,0,0.35)",
              }}
            >
              {/* DATE */}
              <Td color="gray.300" textAlign="center" fontSize="sm" py={3}>
                <Tooltip label={formatDate(tx.date_operation)} hasArrow>
                  <Text fontWeight="medium">
                    {getRelativeTime(tx.date_operation)}
                  </Text>
                </Tooltip>
              </Td>

              {/* TYPE */}
              <Td textAlign="center">
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                  bg={
                    isOut
                      ? "linear-gradient(135deg,#FC8181,#FEB2B2)"
                      : "linear-gradient(135deg,#48BB78,#68D391)"
                  }
                  boxShadow={
                    isOut
                      ? "0 0 12px rgba(252,129,129,0.4)"
                      : "0 0 12px rgba(72,187,120,0.4)"
                  }
                  color="white"
                  fontSize="xs"
                  fontWeight="bold"
                >
                  <Icon as={isOut ? ArrowDownIcon : ArrowUpIcon} />
                  {isOut ? "Outflow" : "Inflow"}
                </Badge>
              </Td>

              {/* AMOUNT */}
              <Td
                color={isOut ? "red.300" : "green.300"}
                fontWeight="extrabold"
                textAlign="center"
                fontSize="md"
                textShadow={
                  isOut
                    ? "0 0 10px rgba(255,90,90,0.3)"
                    : "0 0 10px rgba(90,255,140,0.3)"
                }
              >
                {isOut ? "-" : "+"}
                {tx.amount}
              </Td>

              {/* ASSET */}
              <Td textAlign="center">
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="linear-gradient(135deg,#9F7AEA,#B794F4)"
                  boxShadow="0 0 12px rgba(159,122,234,0.35)"
                  color="white"
                  fontSize="xs"
                >
                  {tx.symbol_crypto}
                </Badge>
              </Td>

              {/* WALLET */}
              <Td textAlign="center">
                <Badge
                  px={3}
                  py={1}
                  borderRadius="full"
                  bg="linear-gradient(135deg,#0BC5EA,#00B5D8)"
                  boxShadow="0 0 12px rgba(0,180,216,0.4)"
                  color="white"
                  fontSize="xs"
                  display="inline-flex"
                  alignItems="center"
                  gap={2}
                >
                  💾 Ledger
                </Badge>
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  )}
</Box>
    </VStack>
  </Box>
);

};

export default Transactions;
